import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Typography,
  Avatar,
  Button,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { RemoveCircle } from "@mui/icons-material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import { CategorySchema } from "../../utils/Schema";
import { GlobalServices } from "../../services/global";
import { trimFormValues } from "../../utils/helpers";
import { IMAGE_BASE_URL } from "../../utils/constants";

const PreviewContainer = styled(Box)({
  position: "relative",
  width: 120,
  height: 120,
  cursor: "pointer",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid #ddd",
  "&:hover .remove-btn": {
    opacity: 1,
  },
});

const RemoveButton = styled(IconButton)({
  position: "absolute",
  top: 2,
  right: 2,
  backgroundColor: "rgba(255,255,255,0.85)",
  opacity: 0,
  transition: "opacity 0.2s",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,1)",
  },
});

function AddEditCategory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [globalsData, setGlobalsData] = useState(null);

  // Single existing uploaded image (filename/URL)
  const [existingImage, setExistingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GlobalServices.getAllGlobalSettings();
      const resData = response?.data?.data || {};
      setGlobalsData(resData);

      if (id) {
        const businessTypes = resData.vendorBusinessTypes || [];
        const currentData = businessTypes[id];

        if (currentData) {
          const catObj = currentData.category || {};
          const existingImg =
            typeof catObj === "string"
              ? ""
              : catObj.image || catObj.categoryImage || null;
          const imageUrl = Array.isArray(existingImg)
            ? existingImg[0]
            : existingImg;

          setExistingImage(imageUrl);
          setData({
            name: typeof catObj === "string" ? catObj : catObj.title || "",
            description:
              typeof catObj === "string" ? "" : catObj.description || "",
          });
        }
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Sync existingImage into preview
  useEffect(() => {
    if (existingImage) {
      setImagePreview(existingImage);
    }
  }, [existingImage]);

  // Helper function to compress images
  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const scaleSize = Math.min(1, maxWidth / img.width);
          canvas.width = img.width * scaleSize;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                resolve(file); // fallback to original if canvas fails
              }
            },
            file.type,
            quality,
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle new image selection
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressedFile = await compressImage(file, 800, 0.7);
        setImageFile(compressedFile);

        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error("Compression failed:", err);
        // Fallback to original
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Remove the current image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setExistingImage(null);
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  // Calculate initial image array
  const getInitialImageArray = () => {
    if (id && data && existingImage) {
      const imageUrl = Array.isArray(existingImage)
        ? existingImage[0]
        : existingImage;
      return imageUrl ? [imageUrl] : [];
    }
    return [];
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: CategorySchema,
    initialValues: {
      name: data?.title || data?.name || "",
      description: data?.description || "",
      status: data?.status || 1,
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);

        // Category is a plain String in the schema
        const newCategoryName = trimmedValues.name;

        if (!globalsData) {
          toast.error("Global data not loaded.");
          setLoading(false);
          return;
        }

        let finalImage = imagePreview || "";

        // Separately upload the image if there's a new file selected
        if (imageFile) {
          const formData = new FormData();
          formData.append("image", imageFile);

          const uploadRes = await GlobalServices.uploadGlobalImage(formData);
          if (uploadRes?.data?.status) {
            finalImage = uploadRes.data.data?.file || uploadRes.data.data?.image || finalImage;
          } else {
            toast.error(uploadRes?.data?.message || "Image upload failed");
            setLoading(false);
            return;
          }
        }

        // Proceed to update the json payload
        const businessTypes = [...(globalsData.vendorBusinessTypes || [])];

        if (id) {
          // Update existing — preserve category object, update title, desc, and image
          if (businessTypes[id]) {
            const existingCat = businessTypes[id].category;
            if (typeof existingCat === "object") {
              businessTypes[id].category = {
                ...existingCat,
                title: newCategoryName,
                description: trimmedValues.description || "",
                image: finalImage
              };
            } else {
              businessTypes[id].category = {
                title: newCategoryName,
                description: trimmedValues.description || "",
                image: finalImage,
                status: "active",
              };
            }
          }
        } else {
          // Add new — category as object, subcategories as [String]
          businessTypes.unshift({
            category: {
              title: newCategoryName,
              description: trimmedValues.description || "",
              image: finalImage,
              status: "active",
            },
            subcategories: [],
          });
        }

        // Format all items: subcategories as array of objects { title, status, description }
        const formattedBusinessTypes = businessTypes.map((item) => {
          const subs = Array.isArray(item.subcategories)
            ? item.subcategories.map((s) => {
              if (typeof s === "object" && s) {
                return { title: s.title || s.name || "", status: s.status || "active", description: s.description || "" };
              }
              return { title: String(s || ""), status: "active", description: "" };
            })
            : [];

          const catTitle = typeof item.category === "string" ? item.category : item.category?.title || "Unnamed";
          const catImg = typeof item.category === "object" ? (item.category.image || "") : "";

          return {
            category: typeof item.category === "object" ? { ...item.category, title: catTitle, image: catImg } : {
              title: catTitle,
              description: "",
              image: "",
              status: "active",
            },
            subcategories: subs,
          };
        });

        // Send FULL globals data to avoid validation errors for missing fields
        const payload = {
          ...globalsData,
          vendorBusinessTypes: formattedBusinessTypes,
        };

        // Clean internal mongo fields before sending
        delete payload._id;
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.__v;

        await GlobalServices.updateGlobalSettings(payload);

        toast.success("Category saved successfully", { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Category</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Category`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Image Upload Section */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <input
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <Box
                    mb={1}
                    display="flex"
                    flexWrap="wrap"
                    gap={2}
                    alignItems="center"
                  >
                    {!imagePreview && (
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ width: "100%" }}
                      >
                        No image uploaded yet
                      </Typography>
                    )}
                    {imagePreview && (
                      <PreviewContainer
                        title="Click to change image"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Avatar
                          src={imagePreview?.startsWith("data:") ? imagePreview : `${IMAGE_BASE_URL}/${imagePreview}`}
                          alt="preview"
                          variant="rounded"
                          sx={{ width: "100%", height: "100%" }}
                        />
                        <RemoveButton
                          size="small"
                          aria-label="Remove image"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="remove-btn"
                        >
                          <RemoveCircle fontSize="small" />
                        </RemoveButton>
                      </PreviewContainer>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {id ? "Edit Image" : "Upload Image"}
                  </Button>
                  {formik.touched.image && formik.errors.image && (
                    <FormHelperText error>{formik.errors.image}</FormHelperText>
                  )}
                </Grid>

                {/* Category Name */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Main Category Name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    required
                    name="name"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    inputProps={{ maxLength: 50 }}
                    placeholder="e.g., SUVs, Sedans, Trucks, Motorcycles"
                  />
                </Grid>

                {/* Description */}
                <Grid size={{ xs: 12 }} md={12} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    name="description"
                    error={Boolean(
                      formik.touched.description && formik.errors.description,
                    )}
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                    inputProps={{ maxLength: 200 }}
                    placeholder="Brief description of the category for SEO and clarity"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12 }}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Category
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditCategory;
